import { useState, useCallback } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { Button, Colors } from "@zapier/design-system";
import { togglesAtomResettable } from "@zapier/canvas-state";
import { toEmbeddableUrl } from "../../../utils/toEmbeddableUrl";
import {
  isImageUrl,
  isVideoUrl as isDirectVideoFile,
} from "@zapier/rich-text-editor";
import { getSandboxAttribute } from "../../../utils/getSandboxAttribute";
import { getCurrentOrigin } from "../../../utils/getCurrentOrigin";
import { ExpandIcon } from "../../icons/ExpandIcon";
import { EmbedContentModal } from "../EmbedContentModal";
import {
  VideoPlayer,
  isValidVideoUrl,
  isValidVideoProviderUrl,
} from "../VideoPlayer";
import { LinkImage } from "../LinkImage";
import styles from "./EmbedContentThumbnail.module.css";

type Props = {
  url: string;
};

type DraggableEmbedOverlayProps = {
  ariaLabel: string;
  onClick: () => void;
};

/**
 * Transparent overlay button that captures clicks while allowing node dragging.
 * Sits on top of iframe to prevent embedded content from interfering with drag interactions.
 */
function DraggableEmbedOverlay({
  ariaLabel,
  onClick,
}: DraggableEmbedOverlayProps) {
  return (
    <button
      type="button"
      className={styles.contentOverlay}
      onClick={onClick}
      aria-label={ariaLabel}
    />
  );
}

/**
 * Displays embedded content (videos, images, documents, spreadsheets) in a thumbnail view.
 *
 * Interaction Behavior:
 * - Images and direct video files (.mp4, .webm, etc.) are displayed without an overlay.
 *   They don't require interaction to activate and can be dragged directly.
 * - Platform video embeds (YouTube, Vimeo, Loom) have an overlay that allows dragging.
 *   When clicked, they begin playing immediately with autoplay.
 * - For other media types (like Spotify, spreadsheets, documents), the user must click once
 *   to activate the embed before they can interact with it.
 * - While the user is interacting with the media, the thumbnail enters a non-draggable state.
 * - When the cursor leaves the thumbnail, the overlay automatically returns so the node can be
 *   dragged again via the thumbnail.
 */
export function EmbedContentThumbnail({ url }: Props) {
  const embeddableUrl = toEmbeddableUrl(url);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveringOverMaximizeButton, setHoveringOverMaximizeButton] =
    useState(false);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [isOverlayHidden, setIsOverlayHidden] = useState(false);

  const isImage = isImageUrl(embeddableUrl);
  const isVideoUrl = isValidVideoUrl(embeddableUrl);
  const isDirectVideo = isDirectVideoFile(embeddableUrl);
  const isPlatformVideoEmbed = isValidVideoProviderUrl(embeddableUrl);

  const { trusted_embed_domains } = useAtomValue(togglesAtomResettable);
  const currentOrigin = getCurrentOrigin();

  const sandboxAttr = getSandboxAttribute(
    url,
    trusted_embed_domains.config.domains,
    currentOrigin,
  );

  const handleMaximize = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onMouseOverMaximizeButton = useCallback((hovering: boolean) => {
    setHoveringOverMaximizeButton(hovering);
  }, []);

  const handleOverlayClick = useCallback(() => {
    if (isPlatformVideoEmbed) {
      // For platform video embeds: enable autoplay (only set once, URL shouldn't change again)
      setShouldAutoplay(true);
    }
    // For iframe embeds: hide overlay to allow interaction
    setIsOverlayHidden(true);
  }, [isPlatformVideoEmbed]);

  const handleMouseLeave = useCallback(() => {
    onMouseOverMaximizeButton(false);
    // Restore overlay to make iframe embeds draggable again
    // Don't reset shouldAutoplay (prevents video restart)
    setIsOverlayHidden(false);
  }, [onMouseOverMaximizeButton]);

  // Determine if overlay should be shown (blocks interaction, enables dragging)
  // Images and direct video files don't need overlay - they don't require interaction to activate
  const shouldShowOverlay = !isImage && !isDirectVideo && !isOverlayHidden;

  return (
    <div
      className={classNames(styles.container)}
      onFocus={() => onMouseOverMaximizeButton(true)}
      onMouseOver={() => onMouseOverMaximizeButton(true)}
      onBlur={() => onMouseOverMaximizeButton(false)}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.iframeWrapper}>
        {isImage ? (
          <LinkImage url={embeddableUrl} variant="thumbnail" />
        ) : isVideoUrl ? (
          <VideoPlayer url={embeddableUrl} shouldAutoplay={shouldAutoplay} />
        ) : (
          <iframe
            src={embeddableUrl}
            title="Embedded content preview"
            className={styles.iframe}
            sandbox={sandboxAttr}
            scrolling="no"
            tabIndex={-1}
          />
        )}
        {shouldShowOverlay && (
          <DraggableEmbedOverlay
            ariaLabel={
              isPlatformVideoEmbed ? "Click to play video" : "Click to interact"
            }
            onClick={handleOverlayClick}
          />
        )}
      </div>
      <span
        className={classNames(styles.buttonWrapper, {
          [styles.hovered]: hoveringOverMaximizeButton,
        })}
      >
        <Button
          size="medium"
          variant="tertiary"
          onPress={handleMaximize}
          iconPosition="start"
        >
          <ExpandIcon fill={Colors.TextDefault} />
          {hoveringOverMaximizeButton ? "Maximize" : null}
        </Button>
      </span>
      <EmbedContentModal
        url={url}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
